import { type MouseEvent, useState } from 'react';
import CheckRounded from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import MicRounded from '@mui/icons-material/MicRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import type { DictationController } from '../hooks/useDictation';
import type { MicrophonePicker } from '../hooks/useMicrophones';
import type { NormalizedDevice } from '../types';

import { DictationLevelBars } from './DictationLevelBars';

export interface DictationButtonProps {
  controller: DictationController;
  /** From `useMicrophones`. Adds the chevron menu when the runtime lists devices. */
  microphones?: MicrophonePicker;
}

function dictationLabel(controller: DictationController): string {
  switch (controller.status) {
    case 'listening':
      return 'Stop dictation (Esc)';
    case 'starting':
      return 'Starting dictation';
    case 'stopping':
      return 'Finishing dictation';
    default:
      return 'Dictate';
  }
}

const ICON = 18;
/** The chevron and the level bars share one slot so nothing moves between states. */
const SLOT = 24;

/** What a screen reader hears; the visible state is the button itself. */
function statusAnnouncement(controller: DictationController): string {
  if (controller.error) return controller.error;
  switch (controller.status) {
    case 'starting':
      return 'Starting the microphone';
    case 'listening':
      return 'Listening. Speak naturally, then press Stop or Escape.';
    case 'stopping':
      return 'Finishing dictation';
    default:
      return '';
  }
}

function MicrophoneMenu({ microphones }: { microphones: MicrophonePicker }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = (event: MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    void microphones.refresh({ interactive: true });
  };
  const close = () => setAnchor(null);
  const choose = (device: NormalizedDevice) => {
    microphones.select(device);
    close();
  };
  return (
    <>
      <Tooltip title="Choose microphone" placement="top" arrow>
        <IconButton
          aria-label="Choose microphone"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          size="small"
          onClick={open}
          sx={{ p: 0.5, color: 'text.secondary' }}
        >
          <KeyboardArrowDownRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, borderRadius: 2 } }, list: { dense: true } }}
      >
        <ListSubheader disableSticky sx={{ lineHeight: '32px' }}>
          Microphone
        </ListSubheader>
        {microphones.loading && microphones.devices.length === 0 && (
          <MenuItem disabled>Looking for microphones…</MenuItem>
        )}
        {!microphones.loading && microphones.devices.length === 0 && (
          <MenuItem disabled sx={{ whiteSpace: 'normal' }}>
            No microphone found. Allow microphone access, or connect one.
          </MenuItem>
        )}
        {microphones.devices.map(device => {
          const selected = microphones.selected
            ? device.internalId === microphones.selected.internalId
            : device.isDefault || device === microphones.devices[0];
          return (
            <MenuItem
              key={device.internalId}
              selected={selected}
              onClick={() => choose(device)}
            >
              <ListItemText
                primary={device.isDefault ? `Default - ${device.displayLabel}` : device.displayLabel}
                slotProps={{ primary: { noWrap: true, sx: { maxWidth: 260 } } }}
              />
              {selected && (
                <ListItemIcon sx={{ minWidth: 0, ml: 1.5, color: 'primary.main' }}>
                  <CheckRounded fontSize="small" />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

/**
 * The microphone control. Idle it is a quiet icon with a small chevron for
 * choosing a device; listening it becomes a red stop button with a soft pulse
 * and the chevron's slot shows the level meter. The footprint never changes
 * between states, so the surrounding text field does not reflow.
 */
export function DictationButton({ controller, microphones }: DictationButtonProps) {
  if (!controller.available) return null;
  const { status } = controller;
  const listening = status === 'listening';
  const label = dictationLabel(controller);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', height: ICON + 8 }}>
      <Box component="span" aria-live="polite" sx={visuallyHidden}>
        {statusAnnouncement(controller)}
      </Box>
      <Tooltip title={label} placement="top" arrow>
        <span>
          <IconButton
            aria-label={label}
            aria-pressed={listening}
            size="small"
            disabled={status === 'stopping'}
            onClick={controller.toggle}
            sx={theme => ({
              p: 0.5,
              color: theme.palette.text.secondary,
              transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
                duration: theme.transitions.duration.shorter,
              }),
              ...(listening && {
                color: theme.palette.error.contrastText,
                bgcolor: theme.palette.error.main,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.18)}`,
                animation: 'jot-dictation-pulse 1.6s ease-in-out infinite',
                '&:hover': { bgcolor: theme.palette.error.dark },
                '@keyframes jot-dictation-pulse': {
                  '0%, 100%': { boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.22)}` },
                  '50%': { boxShadow: `0 0 0 5px ${alpha(theme.palette.error.main, 0.08)}` },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }),
            })}
          >
            {status === 'starting' ? (
              <CircularProgress size={ICON - 2} color="inherit" />
            ) : listening ? (
              <StopRounded sx={{ fontSize: ICON }} />
            ) : (
              <MicRounded sx={{ fontSize: ICON }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Box
        sx={{
          width: SLOT,
          height: SLOT,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {listening ? (
          <DictationLevelBars levelRef={controller.levelRef} active height={12} />
        ) : (
          microphones?.available && status === 'idle' && <MicrophoneMenu microphones={microphones} />
        )}
      </Box>
    </Box>
  );
}
